import { createRouter, createWebHashHistory } from "vue-router";
import { useUserStore } from "@/store/modules/user";
import { useAuthStore } from "@/store/modules/auth";
import { HOME_URL, LOGIN_URL, ROUTER_WHITE_LIST } from "@/config";
import { initDynamicRouter } from "@/router/modules/dynamicRouter";
import { staticRouter, errorRouter } from "@/router/modules/staticRouter";
import NProgress from "@/config/nprogress";
import { getAccessToken } from "@/utils/auth";
import { useDictStoreWithOut } from '@/store/modules/dict'
import { useUserStoreWithOut } from '@/store/modules/user'


/**
 * @description 📚 路由参数配置简介
 * @param path ==> 路由菜单访问路径
 * @param name ==> 路由 name (对应页面组件 name, 可用作 KeepAlive 缓存标识 && 按钮权限筛选)
 * @param redirect ==> 路由重定向地址
 * @param component ==> 视图文件路径
 * @param meta ==> 路由菜单元信息
 * @param meta.icon ==> 菜单和面包屑对应的图标
 * @param meta.title ==> 路由标题 (用作 document.title || 菜单的名称)
 * @param meta.activeMenu ==> 当前路由为详情页时，需要高亮的菜单
 * @param meta.isLink ==> 路由外链时填写的访问地址
 * @param meta.isHide ==> 是否在菜单中隐藏 (通常列表详情页需要隐藏)
 * @param meta.isFull ==> 菜单是否全屏 (示例：数据大屏页面)
 * @param meta.isAffix ==> 菜单是否固定在标签页中 (首页通常是固定项)
 * @param meta.isKeepAlive ==> 当前路由是否缓存
 * */
const router = createRouter({
  history: createWebHashHistory(),
  routes: [...staticRouter, ...errorRouter],
  strict: false,
  scrollBehavior: () => ({ left: 0, top: 0 })
});

/**
 * @description 路由拦截 beforeEach
 * */
router.beforeEach(async (to, from, next) => {
  // 1.NProgress 开始
  NProgress.start();

  // 2.动态设置标题
  const title = import.meta.env.VITE_GLOB_APP_TITLE;
  const token = getAccessToken();
  document.title = to.meta.title ? `${to.meta.title} - ${title}` : title;

  // 3.判断是访问登陆页，有 Token 就在当前页面，没有 Token 重置路由到登陆页
  if (to.path.toLocaleLowerCase() === LOGIN_URL) {
    if (token) return next(from.fullPath);
    resetRouter();
    return next();
  }

  // 4.判断访问页面是否在路由白名单地址(静态路由)中，如果存在直接放行
  if (ROUTER_WHITE_LIST.includes(to.path)) return next();

  // 5.判断是否有 Token，没有重定向到 login 页面
  if (!token) return next({ path: LOGIN_URL, replace: true });

  // 6.有 Token 的情况下，初始化字典和用户信息
  const dictStore = useDictStoreWithOut();
  const userStore = useUserStoreWithOut();
  const authStore = useAuthStore();

  // 初始化字典
  if (!dictStore.getIsSetDict) {
    await dictStore.setDictMap();
  }

  // 初始化用户信息
  if (!userStore.getIsSetUser) {
    await userStore.setUserInfoAction();
  }

  if (to.path.startsWith("/monitoring")) {
    const roles = userStore.getRoles || [];
    if (roles.includes("cadmin")) {
      return next({ path: "/403", replace: true });
    }
    const gid = Array.isArray(to.query.gid) ? to.query.gid[0] : to.query.gid;
    const tidQuery = Array.isArray(to.query.tid) ? to.query.tid[0] : to.query.tid;
    const idQuery = Array.isArray(to.query.id) ? to.query.id[0] : to.query.id;
    const tid = tidQuery || idQuery;
    const name = Array.isArray(to.query.name) ? to.query.name[0] : to.query.name;
    if (gid) {
      return next({
        path: "/group/overview",
        query: { gid, ...(name ? { name } : {}), tab: "monitoring" },
        replace: true
      });
    }
    if (tid) {
      return next({
        path: "/tenant/overview",
        query: { tid, ...(name ? { name } : {}), tab: "monitoring" },
        replace: true
      });
    }
    return next({ path: HOME_URL, replace: true });
  }

  // 7.如果没有菜单列表，就重新请求菜单列表并添加动态路由
  if (!authStore.authMenuListGet.length) {
    await initDynamicRouter();
    return next({ ...to, replace: true });
  }

  // 8.存储 routerName 做按钮权限筛选
  authStore.setRouteName(to.name as string);

  // 9.正常访问页面
  next();
});

/**
 * @description 重置路由
 *
 * 必须连同 authMenuList 一起清空。上面的守卫用「菜单列表是否为空」当作
 * 「动态路由是否已注册」的判据（见第 7 步），若这里只删路由而留下菜单列表，
 * 两者就会脱钩：路由已经没了，守卫却认为不用重新注册，直接放行，
 * 于是任何动态路由都会落到兜底路由，表现为登录后进首页出现 404。
 * */
export const resetRouter = () => {
  const authStore = useAuthStore();
  authStore.flatMenuListGet.forEach(route => {
    const { name } = route;
    if (name && router.hasRoute(name)) router.removeRoute(name);
  });
  authStore.authMenuList = [];
};

/**
 * @description 解析登录后的落地路径
 *
 * 不要写死 HOME_URL：动态路由由角色菜单决定，一旦某个角色的菜单里没有它，
 * 就会静默落到兜底路由（404）而不是给出任何提示。这里先确认它真的能解析到
 * 一个实际路由，否则退回该用户自己菜单里的第一条。
 * */
export const resolveLandingPath = (): string => {
  const resolved = router.resolve(HOME_URL);
  const hitCatchAll = resolved.matched.some(record => record.path.includes(":pathMatch"));
  if (resolved.matched.length > 0 && !hitCatchAll) return HOME_URL;

  const authStore = useAuthStore();
  const fallback = authStore.flatMenuListGet.find(route => {
    if (!route.path || route.meta?.isHide) return false;
    const target = router.resolve(route.path);
    return target.matched.length > 0 && !target.matched.some(r => r.path.includes(":pathMatch"));
  });
  if (fallback?.path) {
    console.warn(`[router] ${HOME_URL} 未注册，登录后改为落地到 ${fallback.path}`);
    return fallback.path;
  }
  return HOME_URL;
};

/**
 * @description 路由跳转错误
 * */
router.onError(error => {
  NProgress.done();
  console.warn("路由错误", error.message);
});

/**
 * @description 路由跳转结束
 * */
router.afterEach(() => {
  NProgress.done();
});

export default router;
